import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Square, Loader2, X, ShoppingBag, Keyboard, RotateCcw, Store as StoreIcon, History, Volume2 } from 'lucide-react';
import { toast } from 'sonner';
import { villageUx, orders as ordersApi, stores as storesApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useCart } from '../lib/cart';
import { useRecorder } from '../lib/useRecorder';
import { fmtStamp } from './LiveClock';

export default function VoiceCartDialog() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [typing, setTyping] = useState(false);
  const [text, setText] = useState('');
  const [lastBasket, setLastBasket] = useState(null);

  const loadReorder = async (transcript) => {
    const lb = lastBasket || await ordersApi.lastBasket().catch(() => null);
    if (!lb?.has_basket) {
      return { transcript, reorder: true, store: null, matches: [], unmatched: [], created_at: new Date().toISOString(), no_history: true };
    }
    const { store } = await storesApi.get(lb.store_id);
    const ok = (lb.items || []).filter((it) => it.available);
    return {
      transcript, reorder: true, store, created_at: new Date().toISOString(),
      matches: ok.map((it) => ({
        product: { id: it.product_id, name: it.name, price: it.price, unit: it.unit, image_url: it.image_url, store_id: it.store_id },
        qty: it.qty || 1, query: it.name, requested: `${it.qty || 1} ${it.unit || ''}`.trim(),
      })),
      unmatched: (lb.items || []).filter((it) => !it.available).map((it) => it.name),
    };
  };

  const handle = async (promise) => {
    setBusy(true);
    try {
      const r = await promise;
      setResult(r.reorder && !r.matches?.length ? await loadReorder(r.transcript) : r);
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not understand. Please try again.'); }
    finally { setBusy(false); }
  };

  const rec = useRecorder((blob, ext) => {
    const fd = new FormData();
    fd.append('file', blob, `voice.${ext}`);
    handle(villageUx.voiceCart(fd));
  });

  useEffect(() => {
    const onOpen = () => {
      if (!user) {
        window.dispatchEvent(new CustomEvent('mandispot:open-auth', { detail: { mode: 'signin' } }));
        toast('Please sign in to order by voice');
        return;
      }
      setResult(null); setTyping(false); setText(''); setOpen(true);
      ordersApi.lastBasket().then(setLastBasket).catch(() => setLastBasket(null));
    };
    window.addEventListener('mandispot:voice-order', onOpen);
    return () => window.removeEventListener('mandispot:voice-order', onOpen);
  }, [user]);

  const close = () => { rec.stop(); setOpen(false); };
  const startRec = () => rec.start().catch((e) => toast.error(e.message || 'Microphone permission denied'));

  if (!open) return null;
  return (
    <div data-testid="voice-cart-dialog" className="fixed inset-0 z-[70] bg-black/50 flex items-end md:items-center justify-center" onClick={close}>
      <div className="bg-white w-full md:max-w-lg rounded-t-3xl md:rounded-3xl p-5 max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Mic className="w-5 h-5 text-[#1B5E20]"/>
          <div className="font-heading font-black text-xl">Bolo aur order karo</div>
          <button data-testid="voice-cart-close" onClick={close} className="ml-auto text-ink/40 hover:text-ink" aria-label="Close"><X className="w-5 h-5"/></button>
        </div>
        <p className="text-xs text-ink/60 mt-1">Say your list in any language — e.g. “2 kilo atta, 1 litre tel, aadha kilo chini”.</p>
        {result ? <VoiceResult result={result} onRetry={() => setResult(null)} onDone={close}/> : (
          <VoiceCapture rec={rec} busy={busy} typing={typing} setTyping={setTyping} text={text} setText={setText}
            onStart={startRec} onSubmitText={() => text.trim() && handle(villageUx.textCart(text))}
            lastBasket={lastBasket} onRepeat={() => handle(loadReorder('Repeat last order'))}/>
        )}
      </div>
    </div>
  );
}

function VoiceCapture({ rec, busy, typing, setTyping, text, setText, onStart, onSubmitText, lastBasket, onRepeat }) {
  if (busy) {
    return (
      <div data-testid="voice-cart-processing" className="py-10 text-center">
        <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#1B5E20]"/>
        <div className="mt-3 font-bold">Listening to your order & finding items…</div>
      </div>
    );
  }
  return (
    <div className="py-6 text-center">
      <button data-testid={rec.recording ? 'voice-cart-stop' : 'voice-cart-record'} onClick={rec.recording ? rec.stop : onStart}
        className={`w-28 h-28 rounded-full mx-auto grid place-items-center text-white shadow-xl active:scale-95 transition-transform ${
          rec.recording ? 'bg-[#B71C1C] animate-pulse' : 'bg-[#1B5E20] hover:bg-[#0D3F12]'}`}>
        {rec.recording ? <Square className="w-10 h-10"/> : <Mic className="w-12 h-12"/>}
      </button>
      <div data-testid="voice-cart-status" className="mt-3 font-black uppercase tracking-wider text-sm">
        {rec.recording ? `Recording… ${rec.seconds}s — tap to stop` : 'Tap the mic and speak'}
      </div>
      <div className="text-[11px] text-ink/50 mt-1">Tip: say “pichli baar wala” to repeat your last order</div>
      {lastBasket?.has_basket && !rec.recording && (
        <button data-testid="voice-cart-repeat-last" onClick={onRepeat}
          className="mt-4 w-full rounded-xl border-2 border-[#E67E22] bg-[#FFF8EE] text-left p-3 flex items-center gap-3 hover:bg-[#FFEFD9]">
          <History className="w-6 h-6 text-[#D35400] shrink-0"/>
          <span className="flex-1 min-w-0">
            <span className="block font-black text-sm">Repeat last order</span>
            <span className="block text-[11px] text-ink/60 truncate">{lastBasket.items?.length || 0} {lastBasket.items?.length === 1 ? 'item' : 'items'} from {lastBasket.store_name} · ₹{Math.round(lastBasket.previous_total || 0)}</span>
          </span>
        </button>
      )}
      <button data-testid="voice-cart-type-toggle" onClick={() => setTyping(!typing)}
        className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-ink/60 hover:text-ink">
        <Keyboard className="w-3.5 h-3.5"/>{typing ? 'Hide typing' : 'Type instead'}
      </button>
      {typing && (
        <div className="mt-3 flex gap-2">
          <input data-testid="voice-cart-text-input" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onSubmitText()}
            placeholder="2 kg atta, 1 litre mustard oil" className="flex-1 min-w-0 border-2 border-ink/20 rounded-lg px-3 h-11 text-sm outline-none focus:border-ink"/>
          <button data-testid="voice-cart-text-submit" onClick={onSubmitText} className="btn-primary !px-4 !py-2 text-sm">Find</button>
        </div>
      )}
    </div>
  );
}

function VoiceResult({ result, onRetry, onDone }) {
  const nav = useNavigate();
  const cart = useCart();
  const [lines, setLines] = useState(() => result.matches.map((m) => ({ ...m })));
  const setQty = (i, q) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, qty: Math.max(0, q) } : l)));
  const total = lines.reduce((s, l) => s + l.product.price * l.qty, 0);
  const count = lines.filter((l) => l.qty > 0).length;
  const summary = result.store
    ? `${result.store.name} से ${count} ${count === 1 ? 'चीज़' : 'चीज़ें'} आपके कार्ट के लिए तैयार ${count === 1 ? 'है' : 'हैं'}। कुल ${Math.round(total)} रुपये।`
    : (result.no_history ? 'आपका पिछला कोई ऑर्डर नहीं मिला।' : 'माफ़ कीजिए, यह सामान अभी पास की दुकान में नहीं मिला।');

  const addAll = () => {
    const items = lines.filter((l) => l.qty > 0).map((l) => ({ product: l.product, qty: l.qty }));
    if (!items.length) return;
    const sameStore = cart.store && cart.store.id === result.store.id;
    if (cart.store && !sameStore && !window.confirm(`Your cart has items from ${cart.store.name}. Replace with ${result.store.name}?`)) return;
    const merged = sameStore ? mergeItems(cart.items, items) : items;
    cart.replaceCart(result.store, merged);
    toast.success(`${items.length} item(s) added to cart`);
    onDone();
    nav('/cart');
  };

  return (
    <div data-testid="voice-cart-result" className="mt-4">
      <HindiSpeaker text={summary} playKey={result.created_at}/>
      <div className="rounded-xl bg-[#F5F5F0] p-3 mt-3">
        <div className="text-[10px] uppercase tracking-[0.2em] font-black text-ink/60">{result.reorder ? 'Repeat order' : 'You said'}</div>
        <div data-testid="voice-cart-transcript" className="font-semibold mt-0.5">“{result.transcript || '—'}”</div>
        <div data-testid="voice-cart-timestamp" className="text-[11px] text-ink/50 mt-1">{fmtStamp(result.created_at, true)}</div>
      </div>
      {result.store ? (
        <>
          <div data-testid="voice-cart-store" className="mt-3 flex items-center gap-2 text-sm font-bold">
            <StoreIcon className="w-4 h-4 text-[#1B5E20]"/>Best shop: {result.store.name}
            <span className="text-ink/50 font-normal">· {result.store.village}</span>
          </div>
          <ul className="mt-2 divide-y divide-ink/10 border-2 border-ink/10 rounded-xl">
            {lines.map((l, i) => (
              <li key={l.product.id} data-testid={`voice-cart-line-${i}`} className="flex items-center gap-2 p-2.5">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{l.product.name}</div>
                  <div className="text-[11px] text-ink/50">you asked {l.requested || l.query} · ₹{l.product.price}/{l.product.unit}</div>
                </div>
                <QtyStepper value={l.qty} onChange={(q) => setQty(i, q)} testid={`voice-cart-qty-${i}`}/>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <div data-testid="voice-cart-no-match" className="mt-3 text-sm text-[#B71C1C] font-bold">
          {result.no_history ? 'No previous order found yet.' : 'No nearby shop has these items yet.'}
        </div>
      )}
      {result.unmatched?.length > 0 && (
        <div data-testid="voice-cart-unmatched" className="mt-2 text-xs text-ink/60">Not found: {result.unmatched.join(', ')}</div>
      )}
      <div className="mt-4 flex gap-2">
        <button data-testid="voice-cart-retry" onClick={onRetry} className="btn-outline !py-2.5 !px-4 text-sm"><RotateCcw className="w-4 h-4"/>Again</button>
        {result.store && lines.length > 0 && (
          <button data-testid="voice-cart-add-all" onClick={addAll} className="btn-primary flex-1 !py-2.5 text-sm">
            <ShoppingBag className="w-4 h-4"/>Add to cart · ₹{total.toFixed(0)}
          </button>
        )}
      </div>
    </div>
  );
}

function HindiSpeaker({ text, playKey }) {
  const [auto, setAuto] = useState(() => localStorage.getItem('ms_voice_autospeak') !== '0');
  const [busy, setBusy] = useState(false);
  const audioRef = useRef(null);
  const urlRef = useRef({ text: null, url: null });

  const play = async () => {
    setBusy(true);
    try {
      if (urlRef.current.text !== text) {
        if (urlRef.current.url) URL.revokeObjectURL(urlRef.current.url);
        urlRef.current = { text, url: URL.createObjectURL(await villageUx.speak(text)) };
      }
      if (!audioRef.current) audioRef.current = new Audio();
      audioRef.current.src = urlRef.current.url;
      await audioRef.current.play();
    } catch { /* autoplay blocked or TTS failed — user can tap the speaker */ }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (auto) play();
    return () => audioRef.current?.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playKey]);
  useEffect(() => () => { if (urlRef.current.url) URL.revokeObjectURL(urlRef.current.url); }, []);

  const toggleAuto = (v) => { setAuto(v); localStorage.setItem('ms_voice_autospeak', v ? '1' : '0'); };

  return (
    <div data-testid="voice-cart-speaker" className="rounded-xl bg-[#E8F5E9] p-3 flex items-center gap-3">
      <button data-testid="voice-cart-speak-btn" onClick={play} disabled={busy} aria-label="Hear summary in Hindi"
        className="w-11 h-11 rounded-full bg-[#1B5E20] text-white grid place-items-center shrink-0 active:scale-95 transition-transform">
        {busy ? <Loader2 className="w-5 h-5 animate-spin"/> : <Volume2 className="w-5 h-5"/>}
      </button>
      <div className="flex-1 min-w-0">
        <div data-testid="voice-cart-hindi-summary" className="text-sm font-bold text-[#1B5E20]">{text}</div>
        <label className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-ink/60 cursor-pointer">
          <input type="checkbox" data-testid="voice-cart-autospeak" checked={auto} onChange={(e) => toggleAuto(e.target.checked)}/>
          Speak automatically
        </label>
      </div>
    </div>
  );
}

function QtyStepper({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      <button onClick={() => onChange(value - 1)} className="w-8 h-8 rounded-lg border-2 border-ink/15 font-black" aria-label="Less">−</button>
      <span className="w-6 text-center font-black tabular-nums">{value}</span>
      <button onClick={() => onChange(value + 1)} className="w-8 h-8 rounded-lg border-2 border-ink/15 font-black" aria-label="More">+</button>
    </div>
  );
}

function mergeItems(existing, incoming) {
  const map = new Map(existing.map((i) => [i.product.id, { ...i }]));
  incoming.forEach((i) => {
    const cur = map.get(i.product.id);
    map.set(i.product.id, cur ? { ...cur, qty: cur.qty + i.qty } : i);
  });
  return [...map.values()];
}
